import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, Trash2, Pencil, Loader2, MapPin, Search, FileSpreadsheet, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const empty = { name: '', own_brand: '', station_ids: [] };
const NONE_DEPT = '__none__';

const AdminZones = () => {
  const [zones, setZones] = useState([]);
  const [stations, setStations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  // Search & filter inside the picker dialog
  const [pickerQ, setPickerQ] = useState('');
  const [pickerDept, setPickerDept] = useState('all');
  const [bulkBusy, setBulkBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/zones'), api.get('/stations'), api.get('/departments'), api.get('/brands')])
      .then(([z, s, d, b]) => { setZones(z.data); setStations(s.data); setDepartments(d.data || []); setBrands(b.data || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const ownBrands = useMemo(() => brands.filter(b => b.is_own).map(b => b.name), [brands]);
  const ownBrandSet = useMemo(() => new Set(ownBrands.map(n => n.toLowerCase())), [ownBrands]);
  const isOwnStation = (s) => s.is_own === true || ownBrandSet.has((s.brand || '').toLowerCase());

  const deptName = (id) => departments.find(d => d.id === id)?.name || '';

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, own_brand: ownBrands[0] || '' });
    setPickerQ(''); setPickerDept('all'); setOpen(true);
  };
  const openEdit = (z) => {
    setEditing(z.id);
    setForm({ name: z.name, own_brand: z.own_brand || ownBrands[0] || '', station_ids: z.station_ids || [] });
    setPickerQ(''); setPickerDept('all');
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    try {
      if (editing) {
        await api.put(`/zones/${editing}`, form);
        toast.success('Zona actualizada');
      } else {
        await api.post('/zones', form);
        toast.success('Zona creada');
      }
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta zona? Los TM asignados quedarán sin zona.')) return;
    try { await api.delete(`/zones/${id}`); toast('Zona eliminada'); load(); }
    catch (e) { toast.error('Error'); }
  };

  const toggleStation = (sid) => {
    setForm(f => ({ ...f, station_ids: f.station_ids.includes(sid) ? f.station_ids.filter(x => x !== sid) : [...f.station_ids, sid] }));
  };

  const downloadSample = async () => {
    try {
      const r = await api.get('/zones/sample-excel', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'muestra_zona_estaciones.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('No se pudo descargar la muestra'); }
  };

  const onBulkFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!editing) {
      toast.error('Primero guarda la zona, luego usa asignación en bulk');
      e.target.value = ''; return;
    }
    const fd = new FormData(); fd.append('file', f);
    setBulkBusy(true);
    try {
      const r = await api.post(`/zones/${editing}/bulk-assign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { matched, added, not_found, total_in_zone } = r.data;
      // Refresh zone data
      const z = await api.get('/zones');
      setZones(z.data);
      const updated = z.data.find(zz => zz.id === editing);
      if (updated) setForm(f => ({ ...f, station_ids: updated.station_ids || [] }));
      toast.success(`${added} estación(es) agregadas · ${matched} encontradas · total ${total_in_zone}`);
      if (not_found && not_found.length > 0) {
        toast.warning(`BUs no encontradas: ${not_found.slice(0, 5).join(', ')}${not_found.length > 5 ? '…' : ''}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar en bulk');
    } finally {
      setBulkBusy(false);
      e.target.value = '';
    }
  };

  // Filter stations by is_own (source of truth) + selected own_brand for propias
  const ownStations = useMemo(() => {
    const selected = (form.own_brand || '').toLowerCase();
    return stations.filter(s => isOwnStation(s) && (!selected || (s.brand || '').toLowerCase() === selected));
  }, [stations, ownBrandSet, form.own_brand]); // eslint-disable-line
  const competitorStations = useMemo(() => stations.filter(s => !isOwnStation(s)), [stations, ownBrandSet]); // eslint-disable-line

  const matchesPicker = (s) => {
    if (pickerQ) {
      const t = pickerQ.toLowerCase();
      const inName = s.name.toLowerCase().includes(t);
      const inBrand = (s.brand || '').toLowerCase().includes(t);
      const inDept = deptName(s.department_id).toLowerCase().includes(t);
      const inBu = (s.bu || '').toLowerCase().includes(t);
      if (!(inName || inBrand || inDept || inBu)) return false;
    }
    if (pickerDept !== 'all') {
      if (pickerDept === NONE_DEPT) { if (s.department_id) return false; }
      else if (s.department_id !== pickerDept) return false;
    }
    return true;
  };

  const ownFiltered = useMemo(() => ownStations.filter(matchesPicker), [ownStations, pickerQ, pickerDept, departments]); // eslint-disable-line
  const compFiltered = useMemo(() => competitorStations.filter(matchesPicker), [competitorStations, pickerQ, pickerDept, departments]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-[36px] leading-tight tracking-tight">Zonas</h2>
          <p className="text-[13px] text-[#5a5a5a] mt-1">{zones.length} zonas configuradas · cada Territory Manager se asigna a una zona.</p>
        </div>
        <Button onClick={openCreate} className="h-10 rounded-full bg-[#1c1c1c] hover:bg-black text-[#f8f6f1] text-[12.5px]" data-testid="open-create-zone"><Plus size={14} className="mr-1.5"/> Nueva zona</Button>
      </div>

      {loading ? <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[#8a8579]"/></div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {zones.map(z => {
            const stationsInZone = stations.filter(s => z.station_ids?.includes(s.id));
            const ownCount = stationsInZone.filter(s => isOwnStation(s)).length;
            return (
              <div key={z.id} className="bg-white border border-[#e8e5de] rounded-xl p-5 lift">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] tracking-wider uppercase text-[#8a8579]">Zona · {z.own_brand || '—'}</p>
                    <h3 className="text-[16px] font-medium tracking-tight truncate">{z.name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(z)} className="w-7 h-7 rounded hover:bg-[#f3f0e9] flex items-center justify-center" data-testid={`edit-zone-${z.id}`}><Pencil size={12}/></button>
                    <button onClick={() => remove(z.id)} className="w-7 h-7 rounded hover:bg-[#fee2e2] flex items-center justify-center text-[#991b1b]" data-testid={`delete-zone-${z.id}`}><Trash2 size={12}/></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-[#e8e5de]">
                  <div>
                    <p className="font-serif text-[24px] leading-none">{stationsInZone.length}</p>
                    <p className="text-[11px] text-[#8a8579] mt-1">total estaciones</p>
                  </div>
                  <div>
                    <p className="font-serif text-[24px] leading-none">{ownCount}</p>
                    <p className="text-[11px] text-[#8a8579] mt-1">propias · {stationsInZone.length - ownCount} comp.</p>
                  </div>
                </div>
              </div>
            );
          })}
          {zones.length === 0 && <p className="col-span-full text-center text-[13px] text-[#8a8579] py-8">Sin zonas. Crea la primera.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="max-w-3xl bg-[#f8f6f1] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? 'Editar zona' : 'Nueva zona'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Nombre</Label>
                <Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Zona Centro" className="mt-1 bg-white" data-testid="zone-name-input"/>
              </div>
              <div><Label className="text-[12px] uppercase tracking-wider text-[#8a8579]">Marca propia</Label>
                <Select value={form.own_brand} onValueChange={v=>setForm({...form,own_brand:v})}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="zone-own-brand-select"><SelectValue placeholder="Selecciona marca"/></SelectTrigger>
                  <SelectContent>
                    {ownBrands.length === 0 && <SelectItem value="__none__" disabled>Sin marcas propias</SelectItem>}
                    {ownBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white border border-[#e8e5de] rounded-lg p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <Label className="text-[11px] uppercase tracking-wider text-[#8a8579] flex items-center gap-1.5"><MapPin size={11}/> Estaciones de la zona ({form.station_ids.length})</Label>
                <div className="flex gap-1.5">
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onBulkFile} className="hidden"/>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={downloadSample}
                    className="h-7 rounded-full border-[#1c1c1c]/20 text-[11px] px-3"
                    data-testid="zone-download-sample-btn"
                  >
                    <Download size={11} className="mr-1"/> Descargar muestra
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={!editing || bulkBusy}
                    className="h-7 rounded-full border-[#1c1c1c]/20 text-[11px] px-3 disabled:opacity-50"
                    title={editing ? 'Asignar estaciones desde Excel' : 'Guarda la zona primero para asignar en bulk'}
                    data-testid="zone-bulk-assign-btn"
                  >
                    <FileSpreadsheet size={11} className="mr-1"/> {bulkBusy ? 'Procesando…' : 'Asignar bulk (Excel)'}
                  </Button>
                </div>
              </div>
              <p className="text-[10.5px] text-[#8a8579] mb-2">Selecciona las estaciones propias y de competencia que componen esta zona. Usa la búsqueda y el filtro por departamento para encontrarlas más rápido.</p>

              {/* Search + department filter */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8a8579]"/>
                  <Input
                    value={pickerQ}
                    onChange={e=>setPickerQ(e.target.value)}
                    placeholder="Buscar por nombre, marca, BU o departamento…"
                    className="pl-8 bg-[#f8f6f1] text-[12px] h-8"
                    data-testid="zone-station-search"
                  />
                </div>
                <Select value={pickerDept} onValueChange={setPickerDept}>
                  <SelectTrigger className="h-8 w-52 bg-[#f8f6f1] text-[12px]" data-testid="zone-station-dept-filter">
                    <SelectValue placeholder="Departamento"/>
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="all">Todos los departamentos</SelectItem>
                    <SelectItem value={NONE_DEPT}>— Sin asignar —</SelectItem>
                    {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(pickerQ || pickerDept !== 'all') && (
                  <button
                    type="button"
                    onClick={()=>{ setPickerQ(''); setPickerDept('all'); }}
                    className="text-[11px] text-[#8a8579] hover:text-[#1c1c1c] underline"
                    data-testid="zone-picker-clear"
                  >
                    Limpiar
                  </button>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1">
                    Propias ({ownFiltered.filter(s => form.station_ids.includes(s.id)).length}/{ownFiltered.length})
                  </p>
                  <div className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="zone-own-list">
                    {ownFiltered.map(s => (
                      <label key={s.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#f3f0e9] cursor-pointer">
                        <input type="checkbox" checked={form.station_ids.includes(s.id)} onChange={() => toggleStation(s.id)} className="w-4 h-4"/>
                        <span className="text-[11.5px] flex-1 truncate">{s.name}</span>
                        {s.department_id && (
                          <span className="text-[9px] text-[#5a5a5a] bg-[#f3f0e9] px-1.5 py-0.5 rounded truncate max-w-[100px]">{deptName(s.department_id)}</span>
                        )}
                        <span className="text-[9.5px] text-[#8a8579]">{s.brand}</span>
                      </label>
                    ))}
                    {ownFiltered.length === 0 && <p className="text-[11px] text-[#a8a397] py-2 px-2">Sin resultados.</p>}
                  </div>
                </div>
                <div>
                  <p className="text-[10.5px] uppercase tracking-wider text-[#8a8579] mb-1">
                    Competidores ({compFiltered.filter(s => form.station_ids.includes(s.id)).length}/{compFiltered.length})
                  </p>
                  <div className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="zone-comp-list">
                    {compFiltered.map(s => (
                      <label key={s.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#f3f0e9] cursor-pointer">
                        <input type="checkbox" checked={form.station_ids.includes(s.id)} onChange={() => toggleStation(s.id)} className="w-4 h-4"/>
                        <span className="text-[11.5px] flex-1 truncate">{s.name}</span>
                        {s.department_id && (
                          <span className="text-[9px] text-[#5a5a5a] bg-[#f3f0e9] px-1.5 py-0.5 rounded truncate max-w-[100px]">{deptName(s.department_id)}</span>
                        )}
                        <span className="text-[9.5px] text-[#8a8579]">{s.brand}</span>
                      </label>
                    ))}
                    {compFiltered.length === 0 && <p className="text-[11px] text-[#a8a397] py-2 px-2">Sin resultados.</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#1c1c1c] text-[#f8f6f1] hover:bg-black" data-testid="zone-save-btn">{editing ? 'Guardar' : 'Crear zona'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminZones;
